// To'lovlar — operator ko'rinishi va bekor qilish.
//
// BACKEND KONTRAKTI (xsib-payments, `admin` paketi):
//   GET  /api/payments?q&state&pspId&from&to&page&size
//        -> { rows, page, size, hasNext }
//   GET  /api/payments/channels          -> [{ id, code, name, state }]
//   GET  /api/payments/{id}              -> PaymentView
//   POST /api/payments/{id}/cancel  { reason } -> OperationResult
//
// ⚠️ YO'L `/api/payments`, `/api/psp` EMAS. `/api/psp/**` — gateway'da
// ANONIM zona (PSP callback'lari uchun, tokensiz). Operator so'rovini
// o'sha prefiksga yozish uni himoyasiz qoldirardi. Bu izoh shu yerda
// turibdi, chunki xato aynan shu faylni nusxalab yozilardi.
//
// ⚠️ `total` YO'Q, `hasNext` bor. `payments.psp_transaction` cheksiz
// o'sadigan jurnal va `COUNT(*)` unda indekssiz qoladi (backend
// `PaymentPage` javadoc'i). Shuning uchun `DataTable` ga
// `showTotal={false}` beriladi va `syntheticTotal` ishlatiladi —
// `ledger` sahifasi bilan bir xil naqsh.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { syntheticTotal } from '@/shared/api/pageResult'
import type { PageResult } from '@/shared/ui/table/DataTable'
import { moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import type { TableState } from '@/shared/ui/table/tableState'

/** `psp_transaction.state` — `NONE` bazada saqlanmaydi, shuning uchun yo'q. */
export const PAYMENT_STATES = ['COMPLETED', 'CANCELLED', 'FAILED'] as const
export type PaymentState = (typeof PAYMENT_STATES)[number]

const paymentSchema = z.object({
  id: uuidSchema,
  channelCode: z.string(),
  channelName: z.string(),
  providerTxnId: z.string(),
  receiptId: z.string().nullish(),
  account: z.string(),
  subscriberId: uuidSchema.nullish(),
  amount: moneySchema,
  state: z.enum(PAYMENT_STATES),
  errorCode: z.string().nullish(),
  balanceAfter: z.number().nullish(),
  ledgerTxnId: z.number().nullish(),
  cancelLedgerTxnId: z.number().nullish(),
  paidAt: z.string().nullish(),
  processedAt: z.string().nullish(),
  createdAt: z.string().nullish(),
  cancelledAt: z.string().nullish(),
  cancelReason: z.string().nullish(),
})

const paymentPageSchema = z.object({
  rows: z.array(paymentSchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

const channelSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  state: z.string(),
})

export interface Payment {
  id: string
  channelCode: string
  channelName: string
  providerTxnId: string
  receiptId: string | null
  account: string
  subscriberId: string | null
  amount: Money
  state: PaymentState
  errorCode: string | null
  /** MINOR birlikda — to'lov PAYTIDAGI balans, hozirgisi EMAS */
  balanceAfter: number | null
  ledgerTxnId: number | null
  /** Teskari (REVERSAL) yozuv — bekor qilingan bo'lsa */
  cancelLedgerTxnId: number | null
  paidAt: string | null
  processedAt: string | null
  createdAt: string | null
  cancelledAt: string | null
  cancelReason: string | null
}

export interface PaymentChannel {
  id: string
  code: string
  name: string
  state: string
}

export interface PaymentFilter {
  state: PaymentState | null
  pspId: string | null
  /** ISO `Instant` — backend UTC kutadi */
  from: string | null
  to: string | null
}

function toPayment(raw: z.infer<typeof paymentSchema>): Payment {
  return {
    id: raw.id,
    channelCode: raw.channelCode,
    channelName: raw.channelName,
    providerTxnId: raw.providerTxnId,
    receiptId: raw.receiptId ?? null,
    account: raw.account,
    subscriberId: raw.subscriberId ?? null,
    amount: toMoney(raw.amount),
    state: raw.state,
    errorCode: raw.errorCode ?? null,
    balanceAfter: raw.balanceAfter ?? null,
    ledgerTxnId: raw.ledgerTxnId ?? null,
    cancelLedgerTxnId: raw.cancelLedgerTxnId ?? null,
    paidAt: raw.paidAt ?? null,
    processedAt: raw.processedAt ?? null,
    createdAt: raw.createdAt ?? null,
    cancelledAt: raw.cancelledAt ?? null,
    cancelReason: raw.cancelReason ?? null,
  }
}

/**
 * Filtrni yopib olgan sahifa yuklovchi — `sitesApi.fetchSitePage` bilan
 * bir xil naqsh (`DataTable` faqat `(state, signal)` beradi).
 */
export function fetchPaymentPage(
  filter: PaymentFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<Payment>> {
  return async (state, signal) => {
    const search = state.search.trim()
    const response = await api.get<unknown>('/api/payments', {
      signal,
      params: {
        q: search === '' ? undefined : search,
        ...(filter.state ? { state: filter.state } : {}),
        ...(filter.pspId ? { pspId: filter.pspId } : {}),
        ...(filter.from ? { from: filter.from } : {}),
        ...(filter.to ? { to: filter.to } : {}),
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    const parsed = paymentPageSchema.parse(response.data)
    return {
      items: parsed.rows.map(toPayment),
      total: syntheticTotal(parsed.page, parsed.size, parsed.rows.length, parsed.hasNext),
    }
  }
}

export async function fetchPaymentChannels(signal?: AbortSignal): Promise<PaymentChannel[]> {
  const response = await api.get<unknown>('/api/payments/channels', { signal })
  return z.array(channelSchema).parse(response.data)
}

/**
 * To'lovni bekor qiladi.
 *
 * ⚠️ Bu MOLIYAVIY amal: `ledger` da TESKARI yozuv paydo bo'ladi va abonent
 * balansi kamayadi. `ledger` o'zgarmas (`DO INSTEAD NOTHING` +
 * `reject_mutation()`), ya'ni "orqaga qaytarish" tugmasi YO'Q — noto'g'ri
 * bekor qilishni faqat yangi tranzaksiya bilan tuzatib bo'ladi.
 *
 * Shuning uchun UI'da tasdiqlash matni terilishi talab qilinadi
 * (`NasDeleteModal` naqshi) va sabab MAJBURIY.
 */
export async function cancelPayment(id: string, reason: string): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/payments/${id}/cancel`, { reason })
  return operationResultSchema.parse(response.data)
}

/** Sabab uzunligi — backend `CancelPaymentRequest` bilan bir xil chegara. */
export const CANCEL_REASON_MAX = 500
export const CANCEL_REASON_MIN = 3
